import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

export function Reports({ api, eventId, classes = [], eventName = 'Event' }) {
    const [selectedClassId, setSelectedClassId] = useState('');
    const [reportFormat, setReportFormat] = useState('json');
    const [showReport, setShowReport] = useState(false);
    const [currentReport, setCurrentReport] = useState(null);
    const [reportType, setReportType] = useState('');

    // Generate class report mutation
    const generateClassReportMutation = useMutation({
        mutationFn: ({ classId, format }) => api.generateClassReport(classId, format),
        onSuccess: (data) => {
            if (data.success) {
                // CSV was downloaded
                return;
            }
            setCurrentReport(data);
            setReportType('class');
            setShowReport(true);
        },
    });

    // Generate event report mutation
    const generateEventReportMutation = useMutation({
        mutationFn: ({ eventId, format }) => api.generateEventReport(eventId, format),
        onSuccess: (data) => {
            if (data.success) {
                // CSV was downloaded
                return;
            }
            setCurrentReport(data);
            setReportType('event');
            setShowReport(true);
        },
    });

    const handleGenerateClassReport = () => {
        if (!selectedClassId) return;
        generateClassReportMutation.mutate({ classId: selectedClassId, format: reportFormat });
    };

    const handleGenerateEventReport = () => {
        generateEventReportMutation.mutate({ eventId, format: reportFormat });
    };

    const handleDownloadCSV = () => {
        if (reportType === 'class') {
            generateClassReportMutation.mutate({ classId: selectedClassId, format: 'csv' });
        } else {
            generateEventReportMutation.mutate({ eventId, format: 'csv' });
        }
    };

    const handleDownloadJSON = () => {
        if (!currentReport) return;

        const dataStr = JSON.stringify(currentReport, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(dataBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType === 'class' ? 'class' : 'event'}_report.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>${reportType === 'class' ? 'Class' : 'Event'} Report</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        .header { text-align: center; margin-bottom: 30px; }
                        .stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 20px; margin-bottom: 30px; }
                        .stat-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
                        .stat-value { font-size: 24px; font-weight: bold; color: #2563eb; }
                        .stat-label { color: #666; margin-top: 5px; }
                        table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
                        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                        th { background-color: #f8f9fa; }
                        .division { margin-bottom: 30px; }
                        .division h3 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 5px; }
                        @media print {
                            .no-print { display: none; }
                        }
                    </style>
                </head>
                <body>
                    ${generatePrintContent()}
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const generatePrintContent = () => {
        if (!currentReport) return '';

        if (reportType === 'class') {
            return `
                <div class="header">
                    <h1>${currentReport.className} Report</h1>
                    <p>Generated: ${new Date(currentReport.generatedAt).toLocaleString()}</p>
                </div>
                
                <div class="stats">
                    <div class="stat-card">
                        <div class="stat-value">${currentReport.statistics.totalRuns}</div>
                        <div class="stat-label">Total Runs</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">${currentReport.statistics.totalParticipants}</div>
                        <div class="stat-label">Total Participants</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">$${currentReport.statistics.totalRevenue.toFixed(2)}</div>
                        <div class="stat-label">Total Revenue</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">${currentReport.statistics.fastestTime ? currentReport.statistics.fastestTime.toFixed(3) + 's' : 'N/A'}</div>
                        <div class="stat-label">Fastest Time</div>
                    </div>
                </div>

                <h2>All Runs (Sorted by Time)</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Place</th>
                            <th>Participant</th>
                            <th>Home Town</th>
                            <th>Horse</th>
                            <th>Time</th>
                            <th>Penalties</th>
                            <th>Final Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${currentReport.allRuns.map((run, index) => `
                            <tr>
                                <td>${index + 1}</td>
                                <td>${run.participantName}</td>
                                <td>${run.homeTown}</td>
                                <td>${run.horseName}</td>
                                <td>${run.time}</td>
                                <td>${run.penalties}</td>
                                <td>${run.finalTime.toFixed(3)}s</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                ${currentReport.divisions.map(division => `
                    <div class="division">
                        <h3>${division.divisionId} Division Results</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>Place</th>
                                    <th>Participant</th>
                                    <th>Home Town</th>
                                    <th>Horse</th>
                                    <th>Time</th>
                                    <th>Penalties</th>
                                    <th>Final Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${division.runs.map(run => `
                                    <tr>
                                        <td>${run.place}</td>
                                        <td>${run.participantName}</td>
                                        <td>${run.homeTown}</td>
                                        <td>${run.horseName}</td>
                                        <td>${run.time}</td>
                                        <td>${run.penalties}</td>
                                        <td>${run.finalTime.toFixed(3)}s</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>
                    </div>
                `).join('')}
            `;
        } else {
            return `
                <div class="header">
                    <h1>${currentReport.eventName} Event Report</h1>
                    <p>Date: ${currentReport.eventDate}</p>
                    <p>Location: ${currentReport.eventLocation}</p>
                    <p>Generated: ${new Date(currentReport.generatedAt).toLocaleString()}</p>
                </div>
                
                <div class="stats">
                    <div class="stat-card">
                        <div class="stat-value">${currentReport.statistics.totalClasses}</div>
                        <div class="stat-label">Total Classes</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">${currentReport.statistics.totalRuns}</div>
                        <div class="stat-label">Total Runs</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">${currentReport.statistics.totalParticipants}</div>
                        <div class="stat-label">Total Participants</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-value">$${currentReport.statistics.totalRevenue.toFixed(2)}</div>
                        <div class="stat-label">Total Revenue</div>
                    </div>
                </div>

                <h2>Class Summary</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Class Name</th>
                            <th>Entry Fee</th>
                            <th>Total Runs</th>
                            <th>Total Participants</th>
                            <th>Total Revenue</th>
                            <th>Divisions</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${currentReport.classes.map(cls => `
                            <tr>
                                <td>${cls.className}</td>
                                <td>$${cls.entryFee.toFixed(2)}</td>
                                <td>${cls.statistics.totalRuns}</td>
                                <td>${cls.statistics.totalParticipants}</td>
                                <td>$${cls.statistics.totalRevenue.toFixed(2)}</td>
                                <td>${cls.divisionCount}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                <h2>All Runs (Overall Ranking)</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Overall Place</th>
                            <th>Class</th>
                            <th>Participant</th>
                            <th>Home Town</th>
                            <th>Horse</th>
                            <th>Time</th>
                            <th>Penalties</th>
                            <th>Final Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${currentReport.allRuns.map(run => `
                            <tr>
                                <td>${run.overallPlace}</td>
                                <td>${run.className}</td>
                                <td>${run.participantName}</td>
                                <td>${run.homeTown}</td>
                                <td>${run.horseName}</td>
                                <td>${run.time}</td>
                                <td>${run.penalties}</td>
                                <td>${run.finalTime.toFixed(3)}s</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;
        }
    };

    return (
        <div className="space-y-6">
            {/* Report Generation Controls */}
            <div className="rounded-lg border bg-card p-6 shadow-sm">
                <div className="flex items-center space-x-2 mb-4">
                    <div className="h-6 w-6 rounded-full bg-blue-500/10 flex items-center justify-center">
                        <span className="text-blue-600 text-sm">📊</span>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground">Generate Reports</h4>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Class Report */}
                    <div className="space-y-4">
                        <h5 className="font-medium text-foreground">Class Report</h5>
                        <div className="space-y-3">
                            <select
                                value={selectedClassId}
                                onChange={(e) => setSelectedClassId(e.target.value)}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                                <option value="">Select a class...</option>
                                {classes.map((cls) => {
                                    // Handle both structures: {id, name} and {classId, name}
                                    const classId = cls.id || cls.classId;
                                    return (
                                        <option key={classId} value={classId}>
                                            {cls.name}
                                        </option>
                                    );
                                })}
                            </select>

                            <select
                                value={reportFormat}
                                onChange={(e) => setReportFormat(e.target.value)}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                                <option value="json">JSON Format</option>
                                <option value="csv">CSV Format</option>
                            </select>

                            <button
                                onClick={handleGenerateClassReport}
                                disabled={!selectedClassId || generateClassReportMutation.isPending}
                                className="w-full inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2"
                            >
                                {generateClassReportMutation.isPending ? 'Generating...' : 'Generate Class Report'}
                            </button>
                        </div>
                    </div>

                    {/* Event Report */}
                    <div className="space-y-4">
                        <h5 className="font-medium text-foreground">Event Report</h5>
                        <div className="space-y-3">
                            <select
                                value={reportFormat}
                                onChange={(e) => setReportFormat(e.target.value)}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            >
                                <option value="json">JSON Format</option>
                                <option value="csv">CSV Format</option>
                            </select>

                            <button
                                onClick={handleGenerateEventReport}
                                disabled={generateEventReportMutation.isPending}
                                className="w-full inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2"
                            >
                                {generateEventReportMutation.isPending ? 'Generating...' : 'Generate Event Report'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Display */}
            {showReport && currentReport && (
                <div className="rounded-lg border bg-card p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <div className="h-6 w-6 rounded-full bg-green-500/10 flex items-center justify-center">
                                <span className="text-green-600 text-sm">📋</span>
                            </div>
                            <h4 className="text-lg font-semibold text-foreground">
                                {reportType === 'class' ? currentReport.className : currentReport.eventName} Report
                            </h4>
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={handlePrint}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-gray-600 text-white hover:bg-gray-700 h-8 px-3"
                            >
                                🖨️ Print
                            </button>
                            <button
                                onClick={handleDownloadJSON}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-blue-600 text-white hover:bg-blue-700 h-8 px-3"
                            >
                                📄 JSON
                            </button>
                            <button
                                onClick={handleDownloadCSV}
                                className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 bg-green-600 text-white hover:bg-green-700 h-8 px-3"
                            >
                                📊 CSV
                            </button>
                        </div>
                    </div>

                    {/* Report Content */}
                    <div className="space-y-6">
                        {/* Statistics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {reportType === 'class' ? (
                                <>
                                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                                        <div className="text-2xl font-bold text-blue-600">{currentReport.statistics.totalRuns}</div>
                                        <div className="text-sm text-blue-600">Total Runs</div>
                                    </div>
                                    <div className="text-center p-4 bg-green-50 rounded-lg">
                                        <div className="text-2xl font-bold text-green-600">{currentReport.statistics.totalParticipants}</div>
                                        <div className="text-sm text-green-600">Participants</div>
                                    </div>
                                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                                        <div className="text-2xl font-bold text-purple-600">${currentReport.statistics.totalRevenue.toFixed(2)}</div>
                                        <div className="text-sm text-purple-600">Revenue</div>
                                    </div>
                                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                                        <div className="text-2xl font-bold text-orange-600">
                                            {currentReport.statistics.fastestTime ? currentReport.statistics.fastestTime.toFixed(3) + 's' : 'N/A'}
                                        </div>
                                        <div className="text-sm text-orange-600">Fastest Time</div>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                                        <div className="text-2xl font-bold text-blue-600">{currentReport.statistics.totalClasses}</div>
                                        <div className="text-sm text-blue-600">Classes</div>
                                    </div>
                                    <div className="text-center p-4 bg-green-50 rounded-lg">
                                        <div className="text-2xl font-bold text-green-600">{currentReport.statistics.totalRuns}</div>
                                        <div className="text-sm text-green-600">Total Runs</div>
                                    </div>
                                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                                        <div className="text-2xl font-bold text-purple-600">{currentReport.statistics.totalParticipants}</div>
                                        <div className="text-sm text-purple-600">Participants</div>
                                    </div>
                                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                                        <div className="text-2xl font-bold text-orange-600">${currentReport.statistics.totalRevenue.toFixed(2)}</div>
                                        <div className="text-sm text-orange-600">Total Revenue</div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Runs Table */}
                        <div>
                            <h5 className="font-medium text-foreground mb-3">
                                {reportType === 'class' ? 'All Runs (Sorted by Time)' : 'All Runs (Overall Ranking)'}
                            </h5>
                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse border border-gray-200">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Place</th>
                                            {reportType === 'event' && <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Class</th>}
                                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Participant</th>
                                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Home Town</th>
                                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Horse</th>
                                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Time</th>
                                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Penalties</th>
                                            <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Final Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentReport.allRuns.slice(0, 10).map((run, index) => (
                                            <tr key={index} className="hover:bg-gray-50">
                                                <td className="border border-gray-200 px-3 py-2 text-sm">
                                                    {reportType === 'class' ? index + 1 : run.overallPlace}
                                                </td>
                                                {reportType === 'event' && (
                                                    <td className="border border-gray-200 px-3 py-2 text-sm">{run.className}</td>
                                                )}
                                                <td className="border border-gray-200 px-3 py-2 text-sm font-medium">{run.participantName}</td>
                                                <td className="border border-gray-200 px-3 py-2 text-sm">{run.homeTown}</td>
                                                <td className="border border-gray-200 px-3 py-2 text-sm">{run.horseName}</td>
                                                <td className="border border-gray-200 px-3 py-2 text-sm">{run.time}</td>
                                                <td className="border border-gray-200 px-3 py-2 text-sm">{run.penalties}</td>
                                                <td className="border border-gray-200 px-3 py-2 text-sm font-mono">{run.finalTime.toFixed(3)}s</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {currentReport.allRuns.length > 10 && (
                                    <p className="text-sm text-gray-500 mt-2">
                                        Showing first 10 runs. Download full report for complete data.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Division Results (for class reports) */}
                        {reportType === 'class' && currentReport.divisions.length > 0 && (
                            <div>
                                <h5 className="font-medium text-foreground mb-3">Division Results</h5>
                                {currentReport.divisions.map((division, index) => (
                                    <div key={index} className="mb-4">
                                        <h6 className="font-medium text-blue-600 mb-2">{division.divisionId} Division</h6>
                                        <div className="overflow-x-auto">
                                            <table className="w-full border-collapse border border-gray-200">
                                                <thead>
                                                    <tr className="bg-gray-50">
                                                        <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Place</th>
                                                        <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Participant</th>
                                                        <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Home Town</th>
                                                        <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Horse</th>
                                                        <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Time</th>
                                                        <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Penalties</th>
                                                        <th className="border border-gray-200 px-3 py-2 text-left text-sm font-medium">Final Time</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {division.runs.map((run, runIndex) => (
                                                        <tr key={runIndex} className="hover:bg-gray-50">
                                                            <td className="border border-gray-200 px-3 py-2 text-sm">{run.place}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm font-medium">{run.participantName}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm">{run.homeTown}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm">{run.horseName}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm">{run.time}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm">{run.penalties}</td>
                                                            <td className="border border-gray-200 px-3 py-2 text-sm font-mono">{run.finalTime.toFixed(3)}s</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
} 